import type { paths } from '@cou/contracts';
import { notifyUnauthorized } from '../session/unauthorized-signal';

/*
 * The Configuration API client (API-10, flows/configuration-management). GET/PUT drive the typed
 * editor; the audit list is the read of configuration_audit_log that admin-ui.md places under
 * API-10. Until CAE-299 lands the handlers, the routes return 501 — callers key off the
 * discriminated result. Injectable at the screen for tests.
 */

type GetConfigBody =
  paths['/api/admin/config']['get']['responses'][200]['content']['application/json'];
type PutConfigBody =
  paths['/api/admin/config']['put']['responses'][200]['content']['application/json'];

export type GetConfigResult =
  | ({ ok: true } & GetConfigBody)
  | { ok: false; status: number; code: string | undefined; message?: string };

export type PutConfigResult =
  | ({ ok: true } & PutConfigBody)
  | { ok: false; status: number; code: string | undefined; message?: string };

/** One configuration_audit_log row (admin-ui.md Audit tab: who / what / when / old → new). */
export interface ConfigAuditEntry {
  readonly id: string;
  readonly key: string;
  readonly oldValue: unknown;
  readonly newValue: unknown;
  readonly changedBy: string;
  readonly changedAt: string;
}

export type GetConfigAuditResult =
  | { ok: true; entries: readonly ConfigAuditEntry[] }
  | { ok: false; status: number; code: string | undefined; message?: string };

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

async function failure(
  response: Response,
): Promise<{ status: number; code: string | undefined; message?: string }> {
  notifyUnauthorized(response.status);
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;
  return {
    status: response.status,
    code: body?.error?.code,
    message: body?.error?.message,
  };
}

function authHeaders(adminToken: string): HeadersInit {
  return {
    'content-type': 'application/json',
    authorization: `Bearer ${adminToken}`,
  };
}

/** Load the registry-governed configuration map (API-10 GET). */
export async function getConfig(adminToken: string): Promise<GetConfigResult> {
  const response = await fetch(`${apiBase()}/api/admin/config`, {
    method: 'GET',
    headers: authHeaders(adminToken),
  });
  if (response.ok) {
    return { ok: true, ...((await response.json()) as GetConfigBody) };
  }
  return { ok: false, ...(await failure(response)) };
}

/** Write one registry key (API-10 PUT). Value is any JSON (seed includes scalars/arrays/objects). */
export async function putConfig(
  adminToken: string,
  key: string,
  value: unknown,
): Promise<PutConfigResult> {
  const response = await fetch(`${apiBase()}/api/admin/config`, {
    method: 'PUT',
    headers: authHeaders(adminToken),
    // Contract stub types value as Record; the documented API and seed use arbitrary JSON.
    body: JSON.stringify({ key, value } satisfies { key: string; value: unknown }),
  });
  if (response.ok) {
    return { ok: true, ...((await response.json()) as PutConfigBody) };
  }
  return { ok: false, ...(await failure(response)) };
}

/**
 * Load the configuration audit log (admin-ui.md: API-10 read of configuration_audit_log).
 * Path is the natural extension of API-10; CAE-241 has no stub yet, so production calls may
 * 501 until CAE-299 — tests inject a client instead.
 */
export async function getConfigAudit(adminToken: string): Promise<GetConfigAuditResult> {
  const response = await fetch(`${apiBase()}/api/admin/config/audit`, {
    method: 'GET',
    headers: authHeaders(adminToken),
  });
  if (response.ok) {
    const body = (await response.json()) as { entries?: ConfigAuditEntry[] };
    return { ok: true, entries: body.entries ?? [] };
  }
  return { ok: false, ...(await failure(response)) };
}

export interface ConfigClient {
  getConfig(adminToken: string): Promise<GetConfigResult>;
  putConfig(adminToken: string, key: string, value: unknown): Promise<PutConfigResult>;
  getConfigAudit(adminToken: string): Promise<GetConfigAuditResult>;
}

export const defaultConfigClient: ConfigClient = {
  getConfig,
  putConfig,
  getConfigAudit,
};
